import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AudiqWordmark, AudiqMark, ThemeToggle, RuleBicolor } from '@audiq/ui';
import type { IdentityField } from '@audiq/shared';
import { LangSwitcher } from '../components/LangSwitcher';
import { VoiceModeToggle, type ChatMode } from '../components/VoiceModeToggle';
import { getActiveFlow, getBranding, startSession } from '../lib/api';

// Sprint 8: bumped a 1.1 per inclusione consenso audio (Deepgram/ElevenLabs).
// I consensi vecchi 1.0 restano validi storicamente; le nuove sessioni richiedono 1.1.
const CONSENT_VERSION = '1.1';

export function StartPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const flowQuery = useQuery({ queryKey: ['flow-active'], queryFn: getActiveFlow });
  const brandingQuery = useQuery({ queryKey: ['branding'], queryFn: getBranding });

  const [identity, setIdentity] = useState<Partial<Record<IdentityField, string>>>({});
  const [chatMode, setChatMode] = useState<ChatMode>('text');
  const [consents, setConsents] = useState({
    data_processing: false,
    extra_eu_transfer: false,
    informativa: false,
    audio_processing: false,
  });
  const [error, setError] = useState<string | null>(null);

  const voiceEnabled = Boolean(brandingQuery.data?.voice_enabled);
  const audioConsentRequired = voiceEnabled && chatMode === 'voice';

  const allConsents =
    consents.data_processing &&
    consents.extra_eu_transfer &&
    consents.informativa &&
    (!audioConsentRequired || consents.audio_processing);

  const startMut = useMutation({
    mutationFn: (payload: Parameters<typeof startSession>[0]) => startSession(payload),
    onSuccess: (res) => {
      const isKiosk = flowQuery.data?.settings?.ask_personal_info?.timing === 'skip';
      sessionStorage.setItem('audiq-session-id', res.session_id);
      sessionStorage.setItem('audiq-pseudonym', res.pseudonym);
      sessionStorage.setItem('audiq-pickup-code', res.pickup_code);
      // Sprint 8: persist chat mode scelto (chat page lo legge per scegliere UX)
      sessionStorage.setItem('audiq-chat-mode', chatMode);
      if (isKiosk) navigate(`/done/${res.pickup_code}`);
      else navigate(`/c/${res.session_id}`);
    },
    onError: () => setError(t('preChat.errors.session_failed')),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allConsents) {
      setError(t('preChat.errors.consents_required'));
      return;
    }
    setError(null);
    const cleanIdentity = Object.fromEntries(
      Object.entries(identity).filter(([, v]) => v && v.trim()),
    );
    startMut.mutate({
      identity: Object.keys(cleanIdentity).length > 0 ? cleanIdentity : undefined,
      consents: {
        data_processing: true,
        extra_eu_transfer: true,
        audio_processing: audioConsentRequired ? consents.audio_processing : false,
        informativa_acknowledged: true,
        consent_text_version: CONSENT_VERSION,
        consent_text_lang: i18n.resolvedLanguage ?? 'it',
      },
    });
  }

  // Stato di errore: nessun flow attivo
  if (flowQuery.isError) {
    return (
      <div
        className="flex flex-1 items-center justify-center p-8 text-center"
        style={{ background: 'var(--surface)', color: 'var(--ink)' }}
      >
        <div>
          <AudiqWordmark size="lg" />
          <p className="mt-4" style={{ color: 'var(--mute)' }}>
            {t('preChat.noActiveFlow')}
          </p>
        </div>
      </div>
    );
  }

  if (flowQuery.isLoading || !flowQuery.data) {
    return (
      <div
        className="flex flex-1 items-center justify-center p-8"
        style={{ background: 'var(--surface)', color: 'var(--mute)' }}
      >
        {t('common.loading')}
      </div>
    );
  }

  const flow = flowQuery.data;
  const askInfo = flow.settings.ask_personal_info;
  const showIdentity = askInfo?.enabled && askInfo.timing === 'at_start';

  return (
    <div
      className="flex flex-1 flex-col"
      style={{ background: 'var(--surface)', color: 'var(--ink)' }}
    >
      <header className="flex items-center justify-between p-4">
        <Link to="/" aria-label="home">
          <AudiqWordmark size="md" />
        </Link>
        <div className="flex items-center gap-2">
          <LangSwitcher />
          <ThemeToggle size="sm" />
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 pb-10">
        <div className="mb-6 flex flex-col items-center text-center">
          <AudiqMark size={64} state="idle" />
          <h1 className="serif mt-4 text-3xl" style={{ color: 'var(--ink)' }}>
            {flow.persona.name}
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--mute)' }}>
            {flow.persona.role}
          </p>
        </div>

        <RuleBicolor className="mb-6" />

        <form onSubmit={onSubmit} className="space-y-5">
          {showIdentity && (
            <section>
              <h2 className="mono mb-3" style={{ color: 'var(--mute)' }}>
                {t('preChat.stepIdentity')}
              </h2>
              <div className="space-y-3">
                {askInfo!.fields.map((field) => (
                  <div key={field}>
                    <label className="mb-1 block text-sm font-medium">
                      {t(`preChat.fields.${field}`)}
                      {askInfo!.required && <span style={{ color: 'var(--danger)' }}> *</span>}
                    </label>
                    <input
                      type={
                        field === 'date_of_birth'
                          ? 'date'
                          : field === 'email'
                            ? 'email'
                            : field === 'phone'
                              ? 'tel'
                              : 'text'
                      }
                      value={identity[field] ?? ''}
                      onChange={(e) =>
                        setIdentity((prev) => ({ ...prev, [field]: e.target.value }))
                      }
                      required={askInfo!.required}
                      className="w-full rounded-md border px-3 py-2 text-sm"
                      style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
                      autoComplete={
                        field === 'first_name'
                          ? 'given-name'
                          : field === 'last_name'
                            ? 'family-name'
                            : field === 'phone'
                              ? 'tel'
                              : field === 'email'
                                ? 'email'
                                : 'off'
                      }
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="mono mb-3" style={{ color: 'var(--mute)' }}>
              {t('preChat.stepConsent')}
            </h2>
            <div className="space-y-3 text-sm">
              <ConsentRow
                checked={consents.data_processing}
                onChange={(v) => setConsents((c) => ({ ...c, data_processing: v }))}
                label={t('preChat.consents.data_processing')}
              />
              <ConsentRow
                checked={consents.extra_eu_transfer}
                onChange={(v) => setConsents((c) => ({ ...c, extra_eu_transfer: v }))}
                label={t('preChat.consents.extra_eu_transfer')}
              />
              <ConsentRow
                checked={consents.informativa}
                onChange={(v) => setConsents((c) => ({ ...c, informativa: v }))}
                label={t('preChat.consents.informativa')}
              />
              {audioConsentRequired ? (
                <ConsentRow
                  checked={consents.audio_processing}
                  onChange={(v) => setConsents((c) => ({ ...c, audio_processing: v }))}
                  label={t('preChat.consents.audio_processing', {
                    defaultValue:
                      "Acconsento al trattamento dei miei dati audio (voce) da parte di Deepgram (USA) per la trascrizione e ElevenLabs (USA) per la sintesi vocale, sotto Standard Contractual Clauses GDPR. L'audio non viene archiviato.",
                  })}
                />
              ) : null}
              <p className="ms-7 mt-2 text-xs">
                <a
                  href="/chat/informativa"
                  className="underline"
                  style={{ color: 'var(--mute)' }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('preChat.consents.readMore')}
                </a>
              </p>
            </div>
          </section>

          {voiceEnabled ? (
            <section>
              <VoiceModeToggle value={chatMode} onChange={setChatMode} />
            </section>
          ) : null}

          {error && (
            <p className="text-sm" style={{ color: 'var(--danger)' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!allConsents || startMut.isPending}
            className="w-full rounded-md px-4 py-3 text-base font-medium disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            {startMut.isPending ? t('common.loading') : t('preChat.submit')}
          </button>
        </form>
      </main>
    </div>
  );
}

function ConsentRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-start gap-3" style={{ color: 'var(--ink)' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <span>{label}</span>
    </label>
  );
}
